import { FuelSpecMinMaxValues } from '@navstation-portal/fuel-specs/data-model/fuel-spec';
import {
  isSectionValid,
  isSectionValidWithOptionalFields,
} from '@navstation-portal/reporting/data-access/vessel-report';
import { BunkerTableFields, OffshoreBunkerTableVm } from '@navstation-portal/reporting/data-model/offshore-report';
import {
  BunkerRowFormState,
  FormSectionState,
  FormSectionStateArray,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { getIsoGradeOptions } from '../utils/fuel-util';

export type OffshoreBunkerVm = ReturnType<typeof buildOffshoreBunkerVm>;

export interface OffshoreBunkerFormVmSections {
  [key: string]: () => FormSectionState;
  options: () => FormSectionState;
  fuelOilBunkers: () => FormSectionState;
  fuelOilBunkerTable: () => FormSectionState;
  debunkered: () => FormSectionState;
  lubeOilBunkers: () => FormSectionState;
  tankStockTable: () => FormSectionState;
}

function getRowMinMax(
  rowIndex: number,
  bunkerRows: BunkerRowFormState[],
  getFuelSpecMinMaxValuesByGrade: (fuelGrade?: string, isoFuelGrade?: string) => FuelSpecMinMaxValues | undefined
): FuelSpecMinMaxValues | undefined {
  const row = bunkerRows?.[rowIndex];
  const fuelGrade = row?.fuelType?.value?.code as string | undefined;
  const isoFuelGrade = row?.isoFuelGrade?.value?.code as string | undefined;
  return getFuelSpecMinMaxValuesByGrade(fuelGrade, isoFuelGrade);
}

export function buildOffshoreBunkerTableVm(
  availableFuelGradeOptions: SelectOption[],
  bunkerRows: BunkerRowFormState[],
  getFuelSpecMinMaxValuesByGrade?: (fuelGrade?: string, isoFuelGrade?: string) => FuelSpecMinMaxValues | undefined
): OffshoreBunkerTableVm {
  const shouldShowInvalidTooltip = (
    fieldState: { status?: string; touched?: boolean } | undefined,
    isEnabled = true
  ): boolean => {
    return !!isEnabled && fieldState?.touched === true && fieldState?.status === 'INVALID';
  };

  const isFuelTypeReadOnly = (index: number) => {
    if (index === 0) return false;
    if (bunkerRows[index - 1]?.fuelType?.value) return false;
    return true;
  };

  const getWarningTooltip = (field: BunkerTableFields, fuelType?: string) => {
    if (field !== 'quantityOrdered') return undefined;
    return `Quantity Ordered ${fuelType || 'Fuel'} should be entered`;
  };

  const getQuantityReceivedWarning = (fuelType?: string, currentQuantityReceived?: unknown): string | undefined => {
    if (typeof currentQuantityReceived === 'number' && currentQuantityReceived > 0) {
      return `${fuelType || 'Fuel'} must be > 0 since the stock value increased from the previous report`;
    }

    return undefined;
  };

  const getInvalidTooltip = (field: BunkerTableFields, fuelType?: string, limits?: FuelSpecMinMaxValues) => {
    const resolvedFuelType = fuelType || 'Fuel';
    const isIsoFuelGradeRequired = !!fuelType && getIsoGradeOptions({ code: fuelType, name: fuelType }).length > 1;

    if (field === 'isoFuelGrade') {
      return isIsoFuelGradeRequired ? 'ISO Fuel Grade is required' : undefined;
    }
    if (field === 'quantityOrdered') {
      return `Quantity Ordered ${resolvedFuelType} should be entered`;
    }
    if (field === 'quantityReceived') {
      return `Quantity Received ${resolvedFuelType} is required`;
    }
    if (field === 'bdnFigures') {
      return `${resolvedFuelType} BDN Figures is required`;
    }

    if (!limits) return `${resolvedFuelType} limits are unavailable`;
    if (field === 'lcv') {
      return typeof limits.lcv_max === 'number'
        ? `Low Calorific Value (LCV) is required and must be between ${limits.lcv_min} and ${limits.lcv_max}`
        : `${resolvedFuelType} LCV is required`;
    }
    if (field === 'density') {
      return typeof limits.density_15_max === 'number'
        ? `Density value is required and must be between ${limits.density_15_min} and ${limits.density_15_max}`
        : 'Density value is required';
    }

    if (field === 'sulphur') {
      const min = limits.sulphur_min;
      const max = limits.sulphur_max;
      if (typeof min === 'number' && typeof max === 'number') {
        return `Sulphur percentage is required and must be between ${min} and ${max}`;
      }
      return 'Sulphur percentage is required';
    }

    if (field === 'viscosity') {
      const min = limits.viscosity_40_min;
      const max = limits.viscosity_40_max;
      if (typeof min === 'number' && typeof max === 'number') {
        return `${resolvedFuelType} viscosity@40C must be between ${min} and ${max}`;
      }
      return `${resolvedFuelType} viscosity is required`;
    }

    return undefined;
  };

  const getFilteredOptions = (rowIndex: number) => {
    const currentRowSelection = bunkerRows[rowIndex]?.fuelType?.value?.code as string | undefined;

    const selectedInOtherRows = new Set(
      bunkerRows.flatMap((row, index) => {
        if (index === rowIndex) return [];

        const code = row?.fuelType?.value?.code as string | undefined;
        return code ? [code] : [];
      })
    );

    return availableFuelGradeOptions.filter((option) => {
      const code = option.code as string | undefined;
      return !!code && (code === currentRowSelection || !selectedInOtherRows.has(code));
    });
  };
  return {
    shouldShowInvalidTooltip,
    isFuelTypeReadOnly,
    getWarningTooltip,
    getQuantityReceivedWarning,
    getInvalidTooltip,
    getRowMinMax: (rowIndex: number) =>
      getRowMinMax(rowIndex, bunkerRows, getFuelSpecMinMaxValuesByGrade || (() => undefined)),
    getFuelTypeOptions: (rowIndex: number) => getFilteredOptions(rowIndex),
  };
}
export function buildOffshoreBunkerVm(state: OffshoreBunkerFormVmSections) {
  const getOptionsCheck = (fieldId: string) => {
    const field = state.options()[fieldId] as StateField<StateFieldType>;
    return (field && 'value' in field ? field.value : false) as boolean;
  };

  const isFuelOilBunkerChecked = () => {
    return getOptionsCheck('tookFuelOil');
  };

  const isLubeOilBunkerChecked = () => {
    return getOptionsCheck('tookLubeOil');
  };

  const isDebunkeredChecked = () => {
    return getOptionsCheck('debunkered');
  };

  const isFuelOilBunkerTableValid = () => {
    const fuelBunkerTableSection = state.fuelOilBunkerTable();
    return isSectionValidWithOptionalFields(fuelBunkerTableSection, true);
  };

  const hasFuelTypeSelected = () => {
    const fuelBunkerTableSection = state.fuelOilBunkerTable();
    if (!fuelBunkerTableSection) return false;
    const bunkerRows = fuelBunkerTableSection['bunkerRows'] as FormSectionStateArray;
    if (!bunkerRows || bunkerRows.length === 0) return false;
    return bunkerRows.some((row) => {
      const fuelTypeField = row['fuelType'] as StateField<StateFieldType>;
      return fuelTypeField && 'value' in fuelTypeField && fuelTypeField.value;
    });
  };

  const fuelOilBunkerTableStatus = () => {
    if (!hasFuelTypeSelected()) return 'valid';

    const fuelOilBunkers = state.fuelOilBunkers() as {
      startedBunkering: StateField<StateFieldType>;
      finishedBunkering: StateField<StateFieldType>;
    };

    const hasBothStartAndFinishDates =
      !!fuelOilBunkers.startedBunkering.value && !!fuelOilBunkers.finishedBunkering.value;
    if (!hasBothStartAndFinishDates) return 'invalid';
    return isFuelOilBunkerTableValid() ? 'valid' : 'invalid';
  };

  const getDebunkerState = (fieldId: string) => {
    return state.debunkered()[fieldId] as StateField<StateFieldType>;
  };

  const debunkeredTableStatus = () => {
    const debunkeredFuelGrade0 = getDebunkerState('debunkeredFuelGrade0');
    const debunkeredFuelGrade1 = getDebunkerState('debunkeredFuelGrade1');
    const debunkeredQuantity0 = getDebunkerState('debunkeredQuantity0');
    const debunkeredQuantity1 = getDebunkerState('debunkeredQuantity1');

    if (
      !debunkeredFuelGrade0?.value &&
      !debunkeredFuelGrade1?.value &&
      debunkeredFuelGrade0.pristine &&
      debunkeredFuelGrade1.pristine
    )
      return 'pending';

    return isSectionValid(
      {
        debunkeredFuelGrade0,
        debunkeredFuelGrade1,
        debunkeredQuantity0,
        debunkeredQuantity1,
      },
      true
    )
      ? 'valid'
      : 'invalid';
  };

  const isFuelOilBunkerValid =
    !isFuelOilBunkerChecked() ||
    (isSectionValidWithOptionalFields(state.fuelOilBunkers(), true) &&
      isSectionValidWithOptionalFields(state.fuelOilBunkerTable(), true));
  const isLubeOilBunkerValid = !isLubeOilBunkerChecked() || isSectionValid(state.lubeOilBunkers(), true);
  const isDebunkerValid = !isDebunkeredChecked() || isSectionValid(state.debunkered(), true);
  const isBunkerValid = isFuelOilBunkerValid && isLubeOilBunkerValid && isDebunkerValid;

  return {
    hasFuelTypeSelected,
    fuelOilBunkerTableStatus,
    debunkeredTableStatus,
    isFuelOilBunkerValid,
    isLubeOilBunkerValid,
    isDebunkerValid,
    isBunkerValid,
    showDebunkered: isDebunkeredChecked(),
  };
}
